import { createContext, useState, useEffect, useRef, ReactNode, useContext, useCallback } from 'react';
import { hallPassApiService } from '../services/hallPassApiService';

export interface HallPassType {
    hallPassTypeId: string;
    organizationId: string | null;
    hallPassTypeName: string;
    hallPassTypeDescription?: string | null;
    hallPassTypeDuration: number;
    soundFileName?: string | null;
    isSummons?: boolean | null;
    isDefault?: boolean | null;
    sortOrder?: number | null;
    createdAt: Date;
    createdBy: string | null;
    updatedAt: Date | null;
    updatedBy: string | null;
    deactivatedOn: Date | null;
    pathId?: string | null;
    path?: {
        pathId: string;
        mapId: string;
        startLocationId: string;
        endLocationId: string;
        waypoints: string;
    } | null;
    startLocationDetails?: {
        locationId: string;
        name: string;
        locationType: string;
        posX: number;
        posY: number;
    } | null;
    destinationLocationDetails?: {
        locationId: string;
        name: string;
        locationType: string;
        posX: number;
        posY: number;
    } | null;
}

interface HallPassTypesContextValue {
    hallPassTypes: HallPassType[];
    fetchHallPassTypes: () => Promise<void>;
}

const HallPassTypesContext = createContext<HallPassTypesContextValue>({
    hallPassTypes: [],
    fetchHallPassTypes: async () => {}
});

export const HallPassTypesProvider = ({ children }: { children: ReactNode }) => {
    const [hallPassTypes, setHallPassTypes] = useState<HallPassType[]>([]);
    const isInitializedRef = useRef(false);

    const fetchHallPassTypes = useCallback(async () => {
        if (isInitializedRef.current) {
            return;
        }
        try {
            const data = await hallPassApiService.getHallPassTypes();
            isInitializedRef.current = true;
            setHallPassTypes(data as unknown as HallPassType[]);
        } catch (err: any) {
            console.error('Error fetching hall pass types:', err.message);
        }
    }, []);

    useEffect(() => {
        fetchHallPassTypes();
    }, [fetchHallPassTypes]);

    return (
        <HallPassTypesContext.Provider value={{ hallPassTypes, fetchHallPassTypes }}>
            {children}
        </HallPassTypesContext.Provider>
    );
};

export const useHallPassTypes = () => useContext(HallPassTypesContext);
